<script setup>
// 泡泡弹出框 我的分组 多选
import useTableView from '@/hooks/use-table-view.js'
import SvgIcon from '@/components/SvgIcon/index.vue'
import tableConfig from '@/views/flowable/my-groups/components/table-config.js'
import {ClickOutside as vClickOutside} from 'element-plus'
import useContactUserStore from '@/store/modules/contact-user.js'
import {storeToRefs} from 'pinia'
import useHomeContextmenu from '@/hooks/use-home-contextmenu.js'

const PopperGroupMembers = defineAsyncComponent(() => import('@/components/popper-my-groups/components/PopperGroupMembers.vue'))
const DrawerMyGroups = defineAsyncComponent(() => import('@/views/flowable/my-groups/components/DrawerMyGroups.vue'))
const DialogAssignSelect = defineAsyncComponent(() => import('@/components/Flow/Task/DialogAssignSelect.vue'))

defineOptions({
  name: 'PopperMyGroups'
})
const {proxy} = getCurrentInstance()
// 此处 modelValue 取 formData的值
const modelValue = defineModel()
const {disabled, readonly, isPrint, title, props, formItem} = defineProps({
  isPrint: Boolean, // 是否打印模式
  disabled: Boolean,
  readonly: Boolean,
  formItem: {type: Object, required: true, default: () => ({})},
  props: { // 需要赋值给formData的参数配置
    type: Object,
    required: true, // 必填，没有这个参数组件会崩溃
    default: () => ({value: '', label: '', group: ''})
  }
})
const emits = defineEmits(['change', 'handleAssignSelect', 'click'])

const {
  dialogRef,
  showDialog,
  visibleDialog,
  tableProps
} = useTableView({}, tableConfig)
provide('tableProps', tableProps)

const {getGroupList, clearGroupList, showContactPopover} = useContactUserStore()
const {groupList} = storeToRefs(useContactUserStore())

const handleDialogClose = async () => {
  clearGroupList()
  await getGroupList()
}

// 分组成员名称 — 提取为独立函数，模板中避免重复计算
const getGroupMembers = (row) => row.sysUsers?.map(u => u.nickName).join(',') ?? ''

// 缓存已选分组 ID 集合，避免在 watch 中重复计算
const selectedIdsSet = computed(() => new Set(modelValue.value?.[props.group] ?? []))
// 已选择的分组列表
const selectList = computed(() => {
  const ids = selectedIdsSet.value
  return ids.size ? groupList.value.filter(item => ids.has(item.id)) : []
})
// 监听 已选中的分组数据，获取成员id,同步 给父级
watch(() => selectList.value, (newVal) => {
  const userIds = newVal?.flatMap(item => item.sysUsers?.map(u => u.userId) ?? [])
  modelValue.value[props.value] = [...new Set(userIds)]
  emits('change', modelValue.value[props.value])
})
// 已选分组成员名单，disabled状态下才使用
const groupMemberList = computed(() => {
  const result = []
  if (disabled && selectList.value.length) {
    const ids = selectList.value.flatMap(item => item.sysUsers)
    ids.forEach(item => {
      const user = result.find(u => u.userId === item.userId)
      if (!user) {
        result.push({userId: item.userId, nickName: item.nickName})
      }
    })
  }
  return result
})

// 切换分组选择状态
const setGroup = (row) => {
  const list = modelValue.value[props.group]
  const index = list.indexOf(row.id)
  index > -1 ? list.splice(index, 1) : list.push(row.id)
}

// 移除分组
const remove = (row) => {
  modelValue.value[props.group] = modelValue.value[props.group].filter(item => item !== row.id)
}

// 选择分组，传递分组id
const handleSelect = (type, row) => {
  const hasValue = type === 'group'
      ? !!modelValue.value[props.label]
      : selectedIdsSet.value.size > 0
  if (!hasValue) {
    return type === 'group' ? setGroup(row) : showAssigneeDialog()
  }
  const msg = type === 'group'
      ? '选择分组后会覆盖此前通过弹窗选定的人员信息'
      : '弹窗选人会替换已选分组数据'
  proxy.$modal.confirm(`${msg}，是否确认覆盖？`, {type: 'warning'}).then(() => {
    modelValue.value[props.value].length = 0
    if (type === 'group') {
      modelValue.value[props.label] = ''
      setGroup(row)
      nextTick(() => {
        visiblePop.value = true
      })
    } else {
      modelValue.value[props.group].length = 0
      showAssigneeDialog()
    }
  }).catch(() => {
  })
}
// 分组成员 也可以通用 右键浮窗的方法
const {
  currentRow,
  visibleContextPopper,
  contextmenuPopRef,
  showContextmenuPopper
} = useHomeContextmenu()

/*选人弹窗选中的数据 用于回显*/
const selectData = ref([])
const visibleAssignDialog = ref(false)

/* 打开選人彈窗
* @params valueKey 用於后端赋值的标识，一般为userId/deptId
* @params labelKey 用於回显的标识，一般为userName/deptName
* */
const showAssigneeDialog = () => {
  const arrNames = modelValue.value?.[props.label]?.split(',')?.filter(item => !!item)
  const arrIds = modelValue.value[props.value] || []
  selectData.value = arrIds.map((item, i) => ({userId: item, nickName: arrNames[i]}))
  visibleAssignDialog.value = true
}

// 清空所有选项
const clear = async () => {
  modelValue.value[props.group].length = 0
  modelValue.value[props.value].length = 0
  modelValue.value[props.label] = ''
}

// 选择负责人后的回调
const handleAssignee = async (val) => {
  selectData.value.length = 0
  let result = {label: '', value: []}
  if (val.length) {
    result.value = val.map(item => item.userId)
    result.label = val.map(item => item.nickName).join(',')
  }
  modelValue.value[props.label] = result.label
  modelValue.value[props.value] = result.value
  emits('change', modelValue.value[props.value])
}

// 分组浮窗
const popoverRef = shallowRef()
const visiblePop = ref(false)
const close = () => {
  visiblePop.value = false
}

// 打开新增分组 drawer
const handleSetting = () => {
  popoverRef.value?.hide()
  showDialog({editFlag: 'add'})
}

const selectNickNameList = computed(() => {
  if (typeof modelValue.value?.[props.label] === 'string') {
    return modelValue.value[props.label].split(',')?.filter(item => !!item)
  }
  return []
})

const handleUserInfo = (e, nickName) => {
  const idx = selectNickNameList.value.indexOf(nickName)
  showContactPopover(e, {userId: modelValue.value[props.value][idx], nickName})
}

getGroupList()
</script>

<template>
  <div :class="['popper-my-groups flex w-100p',{'is-disabled':disabled}]">
    <div class="popper-my-groups__content flex-1">
      <div v-if="modelValue[props.label]">
        <span class="name-span" v-for="(name,index) in selectNickNameList" :key="index"
              @click="handleUserInfo($event,name)">{{ name }}</span>
      </div>
      <div v-else-if="disabled&&selectList.length">
        <div class="flex">
          <span class="name-span" v-for="(item,index) in groupMemberList" :key="index"
                @click="showContactPopover($event,item)">{{ item.nickName }}</span>
        </div>
      </div>
      <template v-else>
        <ElTag class="m-l-10 group-tag" closable v-for="item in selectList" @click="showContextmenuPopper($event, item)"
               @close="remove(item)">{{ item.name }}（{{ item.sysUsers?.length || 0 }}人）
        </ElTag>
      </template>
      <ElButton class="clear-btn" icon="CircleClose" link
                v-if="!disabled&&!readonly&&modelValue[props.value]?.length"
                @click="clear"/>
    </div>
    <!--分组左侧按钮，用于选人弹窗 -->
    <ElButton :disabled="disabled" @click="handleSelect('label')">
      <SvgIcon icon-class="peoples" style="color:inherit"/>
    </ElButton>
    <!--  分组list 浮窗  -->
    <ElPopover ref="popoverRef"
               popper-class="group-popper"
               placement="bottom"
               :visible="visiblePop"
               :width="300"
               trigger="click">
      <div class="more-popper__warp" v-click-outside="close">
        <div class="title">
          <SvgIcon class-name="title-icon" icon-class="user-group" style="color:inherit"/>
          我的分组
        </div>
        <ul v-if="groupList.length" class="my-groups__list">
          <li :class="['my-groups__item',{'is-selected':selectedIdsSet.has(row.id)}]"
              v-for="(row) in groupList"
              :key="row.id" @click="handleSelect('group',row)">
            <ElIcon color="var(--el-color-primary)">
              <SuccessFilled/>
            </ElIcon>
            {{ row.name }}（{{ row.sysUsers?.length || 0 }}人）
            <p v-if="row.sysUsers?.length" class="font-grey group-members">
              {{ getGroupMembers(row) }}</p>
          </li>
        </ul>
        <p v-else class="font-grey font-center">
          -- 暂无分组 --
        </p>
        <div class="my-groups__footer">
          <ElButton link icon="CirclePlus" @click="handleSetting">新增分组</ElButton>
        </div>
      </div>
      <template #reference>
        <ElButton :disabled="disabled" @click="visiblePop = true">
          <SvgIcon icon-class="user-group" style="color:inherit"/>
        </ElButton>
      </template>
    </ElPopover>

    <!--  分组成员  -->
    <PopperGroupMembers v-if="visibleContextPopper" :temp-ref="contextmenuPopRef" v-model="visibleContextPopper"
                        :group="currentRow"
    />
    <!--   新增编辑 drawer-->
    <DrawerMyGroups ref="dialogRef" v-model="visibleDialog"
                    @on-close="handleDialogClose"
    />
    <DialogAssignSelect v-model="visibleAssignDialog" v-if="visibleAssignDialog"
                        :title="formItem.label"
                        current-type="users"
                        :select-data="selectData"
                        width="1000px"
                        @on-close="handleAssignee"
    />
  </div>
</template>
<style lang="scss">
.popper-my-groups {
  //font-size: 0;
  min-height: 30px;
  align-self: flex-start;
  background: var(--el-fill-color-light);
  border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
  box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;

  .popper-my-groups__content {
    position: relative;
    padding-right: 20px;
    padding-left: 12px;
    background-color: #fff;
    color: var(--el-text-color-regular);
    border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;

    .name-span {
      cursor: pointer;

      & + .name-span {
        &:before {
          content: '，';
          color: var(--el-text-color-regular);
        }
      }

      &:hover {
        color: var(--el-color-primary);
      }
    }

    &:hover {
      .clear-btn.el-button {
        display: block;

      }
    }
  }

  .clear-btn.el-button {
    display: none;
    padding: 0;
    position: absolute;
    right: 8px;
    top: 8px;

    &:hover {
      opacity: .8;
    }
  }

  .el-tag.group-tag {
    --el-tag-bg-color: #fff;
    cursor: pointer;

    .el-icon.el-tag__close {
      visibility: hidden;
    }

    &:hover {
      --el-tag-bg-color: var(--el-color-primary-light-9);

      .el-icon.el-tag__close {
        visibility: visible;
      }
    }
  }

  .el-button {
    height: auto;
    flex-shrink: 0;
    border-color: transparent;
    background-color: transparent;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    color: var(--el-color-info);
    padding-left: 5px;
    padding-right: 10px;
    --el-button-disabled-bg-color: transparent;

    .svg-icon {
      font-size: 16px;
    }

    & + .el-button {
      margin-left: 0;
    }
  }

  &.read-only {
    .popper-my-groups__content {
      background: unset;
    }
  }

  &.is-disabled {
    .popper-my-groups__content {
      background: unset;
    }

    .el-button {
      border: 0;
      transition: unset;
      background-color: unset;

      &:hover {
        border-color: transparent;

      }
    }
  }
}


.el-popper.group-popper {
  padding: 0;

  .el-button {
    margin: 10px 0 5px;
  }

  ul.my-groups__list {
    padding: 5px;
    max-height: 200px;
    overflow: auto;

    .my-groups__item {
      cursor: pointer;
      padding: 5px;

      .el-icon {
        display: none;
      }

      .group-members {
        margin: 0;
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &:hover {
        background-color: var(--el-fill-color-light);
      }

      &.is-selected {
        color: var(--el-color-primary-light-3);

        .el-icon {
          display: inline;
        }
      }
    }
  }

  .my-groups__footer {
    padding: 0 5px 5px;
    border-top: 1px solid var(--el-border-color-lighter);
  }
}
</style>
